import type { LoadableDocConfig } from '../config/platformSettings/utils/index';
import { documentTypesByPlatform } from '../config/platformSettings/index';
import { sortPartialDate, sortTime } from 'neuro-utils';
import { fetchWithOptions } from './fetch';
import { parseJWTFromCookie } from './jwtAuthTools';
import type { INeuroDocument } from 'neuro-data-structures';
import { ActionableErrorContext, Loggable, makeLog } from './logger';
import { omitControlProps } from './documentHandling';

const COMMON_ROUTES = [{ name: 'profile' }] satisfies LoadableDocConfig[];

/**
 * Get enabled documents based on platform, as string array
 * @param {ISession} session - Session state
 * @return {LoadableDocConfig[]} Document type array
 */
export const getEnabledDocuments = <T extends Pick<ISessionStore, 'platforms'>>(session: T): LoadableDocConfig[] => {
  const platform: Platform | undefined = session.platforms?.selected;

  let commonRoutes = COMMON_ROUTES;

  // Enabled tiles/documents based on diagnosis
  const diagnosisRoutes = platform ? documentTypesByPlatform[platform] : [];

  // Add diagnosis route to commonRoutes if no platform is selected
  if (!platform) commonRoutes = commonRoutes.concat([{ name: 'diagnosis' }]);

  return commonRoutes.concat(diagnosisRoutes);
};

/**
 * Get documents based on LoadableDocConfig
 * @param {Array<IDocumentV2>} documents - The list of documents to filter
 * @param {LoadableDocConfig} enabledName - The configuration object containing subTypes, filtering rules, etc.
 * @param {string} name - The fallback name to filter by if no subTypes are provided
 * @returns {Array<IDocumentV2>} An array of filtered documents
 */
export const getRelevantDocuments = (
  documents: Array<IDocumentV2>,
  enabledName: LoadableDocConfig | undefined,
  name: string,
): Array<IDocumentV2> => {
  let filteredDocs: IDocumentV2[] = [];

  if (enabledName?.subTypes) {
    const subTypeFilter = (singleDoc: IDocumentV2) => {
      const type = singleDoc._type;
      const data = omitControlProps(singleDoc) as TDocumentDataMap[typeof type];
      const subTypesFiltering = enabledName.subTypesFiltering;
      // const filter = subTypesFiltering && type in subTypesFiltering ? subTypesFiltering[type] : undefined;
      if (subTypesFiltering && type in subTypesFiltering) return subTypesFiltering[type]?.(data as any);
      return true;
      // if (filter) return filter(data);
      // return enabledName.subTypesFiltering?.[singleDoc._type]
      //   ? enabledName.subTypesFiltering[singleDoc._type]?.(singleDoc)
      //   : true;
    };

    for (const doc of documents) {
      // Skip document without commit
      if (!doc._cid) continue;

      // Include documents matching subtypes or secondary documents
      if (
        (enabledName.subTypes.includes(doc._type) && subTypeFilter(doc)) ||
        enabledName.secondaryDocs?.includes(doc._type)
      ) {
        filteredDocs[filteredDocs.length] = doc;
        continue; // Skip further checks once added
      }
    }
  } else {
    // If no subTypes, directly filter by the name
    filteredDocs = documents.filter(doc => doc._type === name && doc._cid);
  }

  return filteredDocs;
};

/**
 * Get navigable routes based on platform, as string array
 * @param {ISession} session - Session state
 * @return {LoadableDocConfig[]} Document type array
 */
export const getNavigableRoutes = (session: ISessionStore): LoadableDocConfig[] => {
  const platform: Platform | undefined = session.platforms?.selected;

  const commonRoutes = COMMON_ROUTES;

  const routesByPlatform = platform ? documentTypesByPlatform[platform] : [];
  const navigableRoutes = routesByPlatform.filter(route => !('nonNavigable' in route && route.nonNavigable));

  return commonRoutes.concat(navigableRoutes);
};

/**
 * Get platform specific section title
 * @param {string} section Section name
 * @param {platform} platform Selected platform
 * @return {string} Section title, 'title' if not specified
 */
export const getSectionTitle = (section: string, platform?: Platform): string => {
  if (
    (section === 'comorbidity' && platform === 'mgravis') ||
    (section === 'clpAndCnpTests' && platform === 'mgravis')
  ) {
    return 'mgravis.title';
  }
  const currentSection = platform
    ? getEnabledDocuments({ platforms: { selected: platform, available: [] } }).find(({ name }) => name === section)
    : { title: undefined };
  return currentSection?.title ?? 'title';
};

export const sortStrengths = (first: { [key: string]: string }, second: { [key: string]: string }): number => {
  // Get the strengths as numbers and compare them. Return 1, 0 or -1 depending on whether the first or the second is bigger
  const s1Key = Object.keys(first)[0];
  const s2Key = Object.keys(second)[0];
  if (!s1Key || !s2Key) return 0;
  const s1 = parseInt(first[s1Key]?.split(' ')[0] ?? '0');
  const s2 = parseInt(second[s2Key]?.split(' ')[0] ?? '0');
  if (s1 > s2) {
    return 1;
  } else if (s1 < s2) {
    return -1;
  } else {
    return 0;
  }
};

/**
 * Filter unfinished documents and return document array with only completed commits
 * @param  {INeuroDocument[]} d - Document array to be filtered
 * @returns {INeuroDocument[]} INeuroDocument
 */
export const filterUnfinishedDocs = (d: INeuroDocument[]): INeuroDocument[] =>
  d.filter(d => d.commits.filter(c => c.commitDate !== null).length !== 0);

/**
 * Function for getting icd10 codes
 * @param ICD10SearchString icd10 search string
 * @param locale locale for which the codes should be
 * @returns array of icd10 codes
 */
export const getICD10Codes = (ICD10SearchString: string, locale: string): Promise<ICD10Item[]> => {
  return fetchWithOptions(
    `/api/icd-code-search/v1/search?search=${ICD10SearchString}&locale=${locale}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET search ICD codes'));
      return [] as ICD10Item[];
    });
};

/**
 * Function for getting description of icd10 code in given locale
 * @param icd10 icd10 code
 * @returns description of icd10 code in given locale
 */
export const getICD10Description = (icd10: string, locale: string): Promise<string | undefined> => {
  const code = encodeURIComponent(icd10);
  return fetchWithOptions(
    `/api/icd-code-search/v1/description?codeVer=icd10&locale=${locale}&code=${code}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) return res.json();
      else return undefined;
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET ICD code description'));
      return undefined;
    });
};

type TSortableDocument = { [field: string]: any } & Partial<IControlProps>;

/**
 * Document sorting function, by default sort from newest to oldest
 * @param {Array<{ type: 'date'|'time'; sortField: string }>} sortingSettings - Array of types and field names to sort documents by
 * @returns {number} Number
 */
export const sortDocuments =
  (sortingSettings: Array<{ type: 'date' | 'time'; sortField: string }>) =>
  (doc1: TSortableDocument, doc2: TSortableDocument): number => {
    let result = 0;

    sortingSettings.forEach(sort => {
      if (result !== 0) return;
      if (sort.type === 'date')
        result = sortPartialDate(doc2[sort.sortField] as PartialDate, doc1[sort.sortField] as PartialDate);
      if (sort.type === 'time') result = sortTime(doc2[sort.sortField] as Time, doc1[sort.sortField] as Time);
    });

    if (result === 0) result = (doc2._cdate ?? 0) - (doc1._cdate ?? 0); // Sort by commitdate
    return result;
  };
